import { combineReducers, configureStore } from "@reduxjs/toolkit"

import { env } from "@/lib/env"
// Importing every endpoint file is what injects its endpoints into the one
// api, so the store must pull them all in even though it names only baseApi.
import "@/lib/storage/api/all-endpoints"

import {
  perfActionsMiddleware,
  perfStoreTimingMiddleware,
} from "@/lib/perf/perf-actions"
import agentSessionsListSlice from "@/lib/storage/agent-sessions-list-slice"
import { baseApi } from "@/lib/storage/api/base-api"
import audioGuideSlice from "@/lib/storage/audio-guide-slice"
import authSlice from "@/lib/storage/auth-slice"
import calendarSyncSlice from "@/lib/storage/calendar-sync-slice"
import collectionSlice from "@/lib/storage/collection-slice"
import creatorSlice from "@/lib/storage/creator-slice"
import { emailVerificationMiddleware } from "@/lib/storage/email-verification-middleware"
import embeddedSlice from "@/lib/storage/embedded-slice"
import flightBookingSlice from "@/lib/storage/flight-booking-slice"
import flightSearchSlice from "@/lib/storage/flight-search-slice"
import { geoMiddleware } from "@/lib/storage/geolocation-middleware"
import { geolocationSlice } from "@/lib/storage/geolocation-slice"
import inspirationSlice from "@/lib/storage/inspiration-slice"
import { itinerarySyncMiddleware } from "@/lib/storage/itinerary-sync-middleware"
import localeSlice from "@/lib/storage/locale-slice"
import notificationsSlice from "@/lib/storage/notifications-slice"
import placeSlice from "@/lib/storage/place-slice"
import profileSlice from "@/lib/storage/profile-slice"
import publicProfileSlice from "@/lib/storage/public-profile-slice"
import { recentItemsSyncMiddleware } from "@/lib/storage/recent-items-sync-middleware"
import { savedContentSyncMiddleware } from "@/lib/storage/saved-content-sync-middleware"
import savedSlice from "@/lib/storage/saved-slice"
import searchResultMapSlice from "@/lib/storage/search-result-map-slice"
import siteNavSlice from "@/lib/storage/site-nav-slice"
import { persistMiddleware } from "@/lib/storage/storage-persist"
import { tripSubscriptionMiddleware } from "@/lib/storage/trip-subscription-middleware"
import { tripSyncMiddleware } from "@/lib/storage/trip-sync-middleware"
import { voiceChatMiddleware } from "@/lib/storage/voice-chat-middleware"
import appSlice from "./app-slice"
import chatExploreAreaSlice from "./chat-explore-area-slice"
import chatModeSlice from "./chat-mode-slice"
import detailSheetSlice from "./detail-sheet-slice"
import dialogSlice from "./dialog-slice"
import draggingSlice from "./dragging-slice"
import { embeddedViewSyncMiddleware } from "./embedded-view-sync-middleware"
import exploreSlice from "./explore-slice"
import folioDocumentSlice from "./folio-document-slice"
import hotelBookingSlice from "./hotel-booking-slice"
import internalAgenticSlice from "./internal-agentic"
import itineraryGenerationSlice from "./itinerary-generation-slice"
import itinerarySlice from "./itinerary-slice"
import mapHoverSlice from "./map-hover-slice"
import navigationSlice from "./navigation-slice"
import presenceSlice from "./presence-slice"
import publicUserSlice from "./public-user-slice"
import tickerSlice from "./ticker-slice"
import tripSlice from "./trip-slice"
import uiSettingsSlice from "./ui-settings-slice"
import userSlice from "./user-slice"
import voiceChatSlice from "./voice-chat-slice"

const rootReducer = combineReducers({
  appSlice,
  authSlice: authSlice.reducer,
  audioGuideSlice: audioGuideSlice.reducer,
  calendarSyncSlice: calendarSyncSlice.reducer,
  userSlice: userSlice.reducer,
  uiSettingsSlice,
  draggingSlice,
  itinerarySlice: itinerarySlice.reducer,
  itineraryGenerationSlice: itineraryGenerationSlice.reducer,
  chatModeSlice: chatModeSlice.reducer,
  chatExploreAreaSlice: chatExploreAreaSlice.reducer,
  mapHoverSlice: mapHoverSlice.reducer,
  detailSheetSlice: detailSheetSlice.reducer,
  flightSearchSlice: flightSearchSlice.reducer,
  flightBookingSlice: flightBookingSlice.reducer,
  inspirationSlice: inspirationSlice.reducer,
  tickerSlice: tickerSlice.reducer,
  tripSlice: tripSlice.reducer,
  folioDocumentSlice: folioDocumentSlice.reducer,
  creatorSlice: creatorSlice.reducer,
  navigationSlice,
  presenceSlice,
  notificationsSlice: notificationsSlice.reducer,
  siteNavSlice: siteNavSlice.reducer,
  savedSlice: savedSlice.reducer,
  searchResultMapSlice: searchResultMapSlice.reducer,
  collectionSlice: collectionSlice.reducer,
  exploreSlice: exploreSlice.reducer,
  profileSlice: profileSlice.reducer,
  publicProfileSlice: publicProfileSlice.reducer,
  publicUserSlice: publicUserSlice.reducer,
  embeddedSlice: embeddedSlice.reducer,
  localeSlice: localeSlice.reducer,
  dialogSlice,
  hotelBookingSlice: hotelBookingSlice.reducer,
  placeSlice: placeSlice.reducer,
  internalAgenticSlice: internalAgenticSlice.reducer,
  agentSessionsListSlice: agentSessionsListSlice.reducer,
  geolocation: geolocationSlice.reducer,
  voiceChatSlice: voiceChatSlice.reducer,
  [baseApi.reducerPath]: baseApi.reducer,
})

/*
 * make sure to set this to a useRef so nextjs will rebuild it for each page, but in the UI it will be the same always
 *
 * ex:
 *
 * const storeRef = useRef<AppStore>()
 * if (!storeRef.current) {
 *   storeRef.current = buildStore()
 * }
 *
 * see: https://redux-toolkit.js.org/usage/nextjs#initial-setup
 * */
const buildStore = (preloadedState?: RootState) => {
  return configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // Overriding ignoredActionPaths replaces RTK's defaults, so keep
          // them: meta.arg holds createAsyncThunk args (we pass `router`
          // into many thunks) and meta.baseQueryMeta is RTK Query internals.
          ignoredActionPaths: [
            "meta.arg",
            "meta.baseQueryMeta",
            "payload.mediaStream",
            "payload.holdCaptureUntil",
          ],
        },
      })
        .concat(baseApi.middleware)
        // innermost: times the reducers and subscribers alone
        .concat(perfStoreTimingMiddleware())
        .prepend(persistMiddleware.middleware)
        .prepend(savedContentSyncMiddleware.middleware)
        .prepend(tripSyncMiddleware.middleware)
        .prepend(tripSubscriptionMiddleware.middleware)
        .prepend(itinerarySyncMiddleware.middleware)
        .prepend(recentItemsSyncMiddleware.middleware)
        .prepend(embeddedViewSyncMiddleware.middleware)
        .prepend(geoMiddleware.middleware)
        .prepend(emailVerificationMiddleware.middleware)
        .prepend(voiceChatMiddleware.middleware)
        // outermost, so its timing covers the whole chain
        .prepend(perfActionsMiddleware()),
    devTools: env("NODE_ENV") === "development",
  })
}

// A named interface keeps incremental tsc from expanding the entire store into
// inferred selector declaration signatures, which can exhaust its heap.
export interface RootState extends ReturnType<typeof rootReducer> {}
export type AppStore = ReturnType<typeof buildStore>
export type AppDispatch = AppStore["dispatch"]

export { buildStore }
